import React, { useCallback } from 'react';
import { Button, Input, message } from 'antd';
import { Table as TableIcon, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useRightPanelContext } from '../RightPanelContext';
import { PANEL_MODE } from '../../constants';
import { convertSingleBlock } from '../../../utils/blockConverter';
import { post } from '../../../../../axios/axios';
import { ResultCard } from '../shared/ResultCard';
import { PreviewActions } from '../shared/PreviewActions';
import styles from '../../../styles/index.module.scss';

const { TextArea } = Input;

const isCanceled = (error) =>
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message?.includes('canceled');

/**
 * 表格修改面板
 */
const TablePanel = () => {
    const {
        editor,
        commonState,
        textState,
        tableState,
        findTableNodeByBlockId,
        saveBlockContent,
        handleCancelPreview,
        handleReset,
    } = useRightPanelContext();

    const contentBlockId =
        textState.selectedBlockIds.length > 0 ? textState.selectedBlockIds[0] : null;

    const handleGenerate = useCallback(async () => {
        if (!editor || commonState.generating) return;
        if (commonState.showPreview) {
            commonState.showPreview = false;
            tableState.previewTableBlock = null;
            tableState.previewOriginalTableContent = '';
            commonState.previewMode = '';
        }
        if (!contentBlockId) {
            message.warning('请先选中表格');
            return;
        }
        const tableInfo = findTableNodeByBlockId(editor, contentBlockId);
        if (!tableInfo) {
            message.warning('找不到表格节点');
            return;
        }
        const tableContent = tableInfo.node.attrs.tableContent;
        if (!tableContent) {
            message.warning('表格内容为空');
            return;
        }

        commonState.generating = true;
        commonState.generationProgress = 20;
        commonState.generationStatus = '正在修改表格…';
        try {
            const response = await post('/ai/ai/paper/editor/v2/modify-table', {
                contentBlockId,
                tableContent,
                userRequirement: textState.customInstruction.trim(),
            });
            commonState.generationProgress = 100;
            commonState.generationStatus = '';
            commonState.generating = false;

            const newBlock = response?.data?.data || response?.data;
            if (newBlock && newBlock.type === 'table') {
                tableState.previewOriginalTableContent = tableContent;
                tableState.previewTableBlock = newBlock;
                commonState.showPreview = true;
                commonState.previewMode = 'modify-table';
            } else {
                message.warning('接口返回数据格式不正确');
            }
        } catch (error) {
            if (isCanceled(error)) return;
            commonState.generating = false;
            commonState.generationStatus = '修表格失败，请重试';
            console.error('修表格接口调用失败:', error);
            message.error(error?.response?.data?.msg || '修表格失败，请重试');
        }
    }, [editor, commonState, textState, tableState, findTableNodeByBlockId, contentBlockId]);

    const handleConfirmApply = useCallback(() => {
        if (!editor || !contentBlockId) return;
        try {
            const newTableNode = convertSingleBlock(tableState.previewTableBlock);
            if (!newTableNode) throw new Error('转换新块失败');
            const tableInfo = findTableNodeByBlockId(editor, contentBlockId);
            if (!tableInfo) throw new Error('找不到原表格节点');

            const { node: oldTableNode, pos } = tableInfo;
            editor
                .chain()
                .focus()
                .setTextSelection({ from: pos, to: pos + oldTableNode.nodeSize })
                .deleteSelection()
                .insertContent(newTableNode)
                .run();

            message.success('表格已更新');
            saveBlockContent(contentBlockId);

            commonState.showPreview = false;
            tableState.previewTableBlock = null;
            tableState.previewOriginalTableContent = '';
            commonState.previewMode = '';
            commonState.mode = PANEL_MODE.EMPTY;
            textState.selectedText = '';
            textState.selectedBlockIds = [];
            textState.customInstruction = '';
            textState.currentAction = '';
            textState.selectedRange = { from: null, to: null };
        } catch (error) {
            console.error('替换表格节点失败:', error);
            message.error(error.message || '替换表格节点失败');
        }
    }, [
        commonState,
        textState,
        tableState,
        editor,
        findTableNodeByBlockId,
        saveBlockContent,
        contentBlockId,
    ]);

    if (commonState.showPreview && tableState.previewTableBlock) {
        return (
            <div className={styles.panelSection}>
                <ResultCard
                    title="修表结果"
                    meta="表格"
                    actions={
                        <PreviewActions
                            onCancel={handleCancelPreview}
                            onRegenerate={handleGenerate}
                            onApply={handleConfirmApply}
                            generating={commonState.generating}
                            applyText="替换表格"
                        />
                    }
                >
                    <div className={styles.previewTableBox}>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                table: ({ children }) => (
                                    <table className={styles.previewTable}>{children}</table>
                                ),
                            }}
                        >
                            {tableState.previewTableBlock.content}
                        </ReactMarkdown>
                    </div>
                </ResultCard>
            </div>
        );
    }

    return (
        <div className={styles.panelSection}>
            <div className={styles.quoteCard}>
                <div className={styles.quoteCardLabel}>
                    <TableIcon size={14} />
                    已选中表格
                </div>
                <div className={styles.quoteCardText}>
                    可以直接在正文里编辑单元格；也可以让 AI 按要求调整表格结构和数据。
                </div>
            </div>

            <div className={styles.sectionLabel}>修改要求</div>
            <TextArea
                value={textState.customInstruction}
                onChange={(e) => {
                    textState.customInstruction = e.target.value;
                }}
                placeholder="例如：增加一列「占比」、把数据按年份排序…"
                autoSize={{ minRows: 3, maxRows: 6 }}
                disabled={commonState.generating}
                className={styles.instructionInput}
            />

            {commonState.generationStatus && (
                <div className={styles.generationStatus}>{commonState.generationStatus}</div>
            )}

            <Button
                type="primary"
                block
                size="large"
                icon={<Sparkles size={16} />}
                onClick={handleGenerate}
                loading={commonState.generating}
                className={styles.primaryAction}
            >
                {commonState.generating ? 'AI 处理中…' : '开始修表格'}
            </Button>
            <Button block type="text" onClick={handleReset} disabled={commonState.generating}>
                取消
            </Button>
        </div>
    );
};

export default TablePanel;
